/**
 * useShoppingWS — open /ws, subscribe to topic 'shopping', merge incoming
 * events into the TanStack Query cache by id+version (Plan 04-03).
 *
 * Pitfall 4 mitigation (id+version dedup): if the cached row's `version` is
 * already >= the incoming message's `item.version`, we ignore — this is
 * the originator's own broadcast echoing back, or an out-of-order replay.
 *
 * Server payloads (Plan 04-02 D-08):
 *   - added     { id, addedBy,    item }   — append to cache (skip if already present)
 *   - updated   { id, updatedBy,  item }   — replace cached row by id (version-gated)
 *   - checked   { id, checkedBy,  item }   — same merge as updated
 *   - unchecked { id, uncheckedBy, item }  — same merge as updated
 *   - removed   { id, removedBy }          — drop by id (no row body)
 *
 * Gjenoppkobling og backoff: se lib/reconnectingSocket.ts.
 *
 * The WS upgrade requires the same session cookie as /api/* routes.
 *
 * After a successful reconnect, callers may also fire a `?since=<lastUpdatedAt>`
 * delta query to absorb anything missed while disconnected (Plan 04-04 SW
 * wires this; Plan 04-03 ships the WS subscription only).
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ShoppingItem } from '@stell/shared';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

type ShoppingWSEvent =
  | { event: 'added'; id: string; item: ShoppingItem }
  | { event: 'updated'; id: string; item: ShoppingItem }
  | { event: 'checked'; id: string; item: ShoppingItem }
  | { event: 'unchecked'; id: string; item: ShoppingItem }
  | { event: 'removed'; id: string };

const QK = ['shopping', 'items'] as const;

/**
 * Pure cache-merge for an incoming WS message — exported for unit tests.
 * Returns the updated array (or the same reference when no change).
 */
export function mergeShoppingEvent(
  cache: ShoppingItem[],
  msg: ShoppingWSEvent,
): ShoppingItem[] {
  switch (msg.event) {
    case 'added': {
      const existingIdx = cache.findIndex((x) => x.id === msg.item.id);
      if (existingIdx === -1) return [...cache, msg.item];
      const existing = cache[existingIdx] as ShoppingItem & { _pending?: boolean };
      // Plan 04-04: replace optimistic ghost (`_pending: true` from
      // useShoppingMutations.addItem.onMutate) with the canonical server
      // row when the WS broadcast arrives — this is what clears the
      // <OfflinePendingChip> after Workbox queue replay succeeds.
      if (existing._pending === true) {
        const next = cache.slice();
        next[existingIdx] = msg.item;
        return next;
      }
      // Otherwise dedup — same row already in canonical state.
      return cache;
    }
    case 'updated':
    case 'checked':
    case 'unchecked': {
      let changed = false;
      const next = cache.map((x) => {
        if (x.id !== msg.item.id) return x;
        if (x.version >= msg.item.version) return x; // dedup by version (Pitfall 4)
        changed = true;
        return msg.item;
      });
      // If the row didn't exist locally yet, treat as add (offline-then-WS path).
      if (!changed && !cache.some((x) => x.id === msg.item.id)) {
        return [...cache, msg.item];
      }
      return changed ? next : cache;
    }
    case 'removed': {
      if (!cache.some((x) => x.id === msg.id)) return cache;
      return cache.filter((x) => x.id !== msg.id);
    }
    default:
      return cache;
  }
}

export function useShoppingWS(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const env = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: unknown;
        };
        if (env?.topic !== 'shopping') return;
        // The hub envelope places the broadcast payload on `data`.
        const payload = env.data as Record<string, unknown> | undefined;
        if (!payload || typeof env.event !== 'string') return;

        let msg: ShoppingWSEvent | null = null;
        switch (env.event) {
          case 'added':
          case 'updated':
          case 'checked':
          case 'unchecked': {
            const item = payload.item as ShoppingItem | undefined;
            const id = payload.id as string | undefined;
            if (!item || typeof id !== 'string') return;
            msg = { event: env.event, id, item };
            break;
          }
          case 'removed': {
            const id = payload.id as string | undefined;
            if (typeof id !== 'string') return;
            msg = { event: 'removed', id };
            break;
          }
          default:
            return;
        }
        qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
          mergeShoppingEvent(old, msg as ShoppingWSEvent),
        );
      } catch {
        // Malformed frame — drop silently.
      }
    };

    return openReconnectingSocket({ topics: ['shopping'], onMessage });
  }, [qc]);
}
